import React, { useEffect, useRef, useState } from "react";
import { useAppSelector } from "store";
import { useTranslation } from "react-i18next";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import TextEditor from "components/wysiwyg/Wysiwyg";
import instance from "api/apiClient";
import { ILocaleData } from "configs/types";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

interface PaymentProviderLocalesTabProps {
  id?: string;
  currentData?: ILocaleData;
  handleData: (e: ILocaleData) => void;
  onInitialData: (e: ILocaleData) => void;
  url: string;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  onLoadingChange: (isLoading: boolean) => void;
  isReadOnly: boolean;
}
const ProvidersLocalesTab: React.FC<PaymentProviderLocalesTabProps> = ({
  id,
  handleData,
  onInitialData,
  currentData,
  url,
  fieldErrors,
  clearFieldError,
  onLoadingChange,
  isReadOnly,
}) => {
  const localesList = useAppSelector((store) => store.settings.locales);
  const { t } = useTranslation();
  const canEdit = !isReadOnly;

  const [activeLocale, setActiveLocale] = useState<string>();
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const requestVersion = useRef(0);
  const callbacks = useRef({ handleData, onInitialData, onLoadingChange });
  callbacks.current = { handleData, onInitialData, onLoadingChange };

  useEffect(() => {
    if (localesList.length) {
      setActiveLocale(localesList[0]);
    }
  }, [localesList]);

  useEffect(() => {
    const version = ++requestVersion.current;
    if (!id || !activeLocale) {
      setIsFetched(false);
      callbacks.current.onLoadingChange(false);
      return;
    }

    setIsFetched(false);
    callbacks.current.onLoadingChange(true);

    instance
      .get(`${url}/${id}/?locale=${activeLocale}`)
      .then((result) => {
        if (version !== requestVersion.current) return;
        const responseData = result?.data?.data ?? result?.data ?? result;
        const translation: ILocaleData = {
          ...responseData,
          geo: responseData?.geo ?? "",
          locale: responseData?.locale ?? activeLocale,
          title: responseData?.title ?? "",
          content: responseData?.content ?? responseData?.description ?? "",
          isActive: Boolean(responseData?.isActive),
        };
        callbacks.current.handleData(translation);
        callbacks.current.onInitialData(translation);
      })
      .catch((error) => {
        if (version !== requestVersion.current) return;
        console.error("Failed to load payment provider translation", error);
        const emptyTranslation: ILocaleData = {
          geo: "",
          locale: activeLocale,
          title: "",
          content: "",
          isActive: false,
        };
        callbacks.current.handleData(emptyTranslation);
        callbacks.current.onInitialData(emptyTranslation);
      })
      .finally(() => {
        if (version !== requestVersion.current) return;
        setIsFetched(true);
        callbacks.current.onLoadingChange(false);
      });

    return () => {
      requestVersion.current += 1;
    };
  }, [id, activeLocale, url]);

 return (
   <div className="cms-page-tab">
     <div className="game-modal-tabs small mt-14">
       {localesList.map((el) => (
         <div
           className={`tab-item ${activeLocale === el ? "active" : ""}`}
           key={el}
           onClick={() => {
             if (el === activeLocale) return;
             setActiveLocale(el);
             clearFieldError("title");
             clearFieldError("content");
             clearFieldError("isActive");
           }}
         >
           <img
             src={`https://flagpedia.net/data/flags/icon/16x12/${el
               .split("-")[1]
               ?.toLowerCase()}.png`}
             alt=""
           />
           {t(el).replace(/-[a-z]+$/i, "")}
         </div>
       ))}
     </div>

     {!isFetched ? (
       <ModalTabSkeleton />
     ) : (
       <div className="pages-form-content mt-14">
         <div className="row">
           <div className="bonus-form-box--item flex-1">
             <InnerInput
              value={currentData?.title ?? ""}
               placeholder={t("title")}
               label={t("title")}
               type="text"
               id="banners-title"
              onChange={(e) => {
                 if (!currentData) return;
                 handleData({ ...currentData, title: e });
                 clearFieldError("title");
               }}
                hasError={Boolean(fieldErrors?.["title"])}
                disabled={!canEdit}
             />
           </div>

           <div className="bonus-form-box--item flex-1">
             <SwitchBtn
               activeTxt={t("active")}
               isActive={currentData?.isActive}
                handleClick={() => {
                  if (!currentData) return;
                 handleData({
                   ...currentData,
                   isActive: !currentData?.isActive,
                 });
                 clearFieldError("isActive");
               }}
                hasError={Boolean(fieldErrors?.["isActive"])}
                disabled={!canEdit}
             />
           </div>
         </div>

         <div className="bonus-form-box--item html-editor">
           <div className="label">{t("content")}</div>

           <TextEditor
             value={currentData?.content || ""}
              hasError={Boolean(fieldErrors?.["content"])}
              onChange={(html) => {
                if (!currentData) return;
                handleData({ ...currentData, content: html });
                clearFieldError("content");
              }}
              disabled={!canEdit}
           />
         </div>
       </div>
     )}
   </div>
 );
};

export default ProvidersLocalesTab;
